// The notification bell (server docs: notifications.md; client contract:
// widgets.md "The notification bell"). The badge polls
// /frm/notifications/unread ONCE per page load ({ progress: false } — a
// background poll, never the top bar); the dropdown fetches the newest
// rows on every open, so what it shows is current without any push.
// Clicking a row marks it read (keepalive — the write survives the
// navigation) and follows its destination: `url` verbatim, or `route` +
// `params` through g.route.url so the context prefix and language fill
// in the way url() does everywhere else. A destination is always a
// navigation — never an action.

function esc(value) {
	return String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

export function initNotifications() {
	const bell = document.getElementById('notificationsBell');
	if (!bell || !window.g || !window.g.fetch || !window.g.user) return;
	const menu = bell.parentElement.querySelector('.notifications-menu');
	const badge = bell.querySelector('.topbar-badge');
	const list = document.getElementById('notificationsList');
	const readAll = menu ? menu.querySelector('.notifications-read-all') : null;
	if (!menu || !badge || !list) return;

	let rows = [];
	let unread = 0;

	function setBadge(n) {
		unread = Math.max(0, n);
		badge.textContent = unread > 99 ? '99+' : String(unread || '');
		badge.hidden = unread <= 0;
		if (readAll) readAll.hidden = unread <= 0;
	}

	function destination(row) {
		if (row.url) return row.url;
		if (row.route) {
			try {
				return window.g.route.url(row.route, row.params || {});
			} catch (e) {
				return null; // a bad destination never breaks the bell
			}
		}
		return null;
	}

	function render() {
		if (!rows.length) {
			list.innerHTML = '<div class="notifications-empty">' + esc(window.g.translate('Nothing new yet.')) + '</div>';
			return;
		}
		list.innerHTML = rows.map((row) => {
			const meta = [window.g.format.relative(row.updatedAt), row.company].filter(Boolean).join(' · ');
			return '<button type="button" class="search-result notification-item' + (row.read ? '' : ' is-unread') + '" data-id="' + esc(row.id) + '">'
				+ '<span class="search-result-icon"><i class="' + esc(row.icon || 'ri-notification-3-line') + '" aria-hidden="true"></i></span>'
				+ '<span class="search-result-text"><span class="notification-item-text">' + esc(row.text) + '</span>'
				+ '<span class="search-result-meta">' + esc(meta) + '</span></span>'
				+ '</button>';
		}).join('');
	}

	let loadSeq = 0; // stale responses drop (the race-conditions scan): an
	// older list landing after mark-all-read used to resurrect the unread
	// badge and dots from its pre-read snapshot
	async function load() {
		const mine = ++loadSeq;
		const res = await window.g.fetch('/frm/notifications/list', {}, { progress: false });
		if (mine !== loadSeq) return; // a newer load or a read-all owns the view
		if (!res.success || res.error.code !== 0) {
			list.innerHTML = '<div class="notifications-empty">' + esc(res.error.message || window.g.translate('Something went wrong.')) + '</div>';
			return;
		}
		rows = res.data.notifications || [];
		setBadge(res.data.unread || 0);
		render();
	}

	bell.addEventListener('show.bs.dropdown', load);

	list.addEventListener('click', (event) => {
		const item = event.target.closest('.notification-item');
		if (!item) return;
		const row = rows.find((r) => String(r.id) === item.getAttribute('data-id'));
		if (!row) return;
		if (!row.read) {
			row.read = true;
			item.classList.remove('is-unread');
			setBadge(unread - 1);
			window.g.fetch('/frm/notifications/read', { id: row.id }, { progress: false, keepalive: true });
		}
		const dest = destination(row);
		if (dest) window.location.assign(dest);
	});

	if (readAll) {
		readAll.addEventListener('click', async () => {
			const res = await window.g.fetch('/frm/notifications/read-all', {}, { progress: false });
			if (!res.success || res.error.code !== 0) return;
			loadSeq++; // an in-flight list from before the read-all is stale now
			rows = rows.map((r) => ({ ...r, read: true }));
			setBadge(0);
			render();
		});
	}

	// the page-load poll: the badge is the only thing worth a request
	// before the user opens the dropdown
	(async () => {
		const res = await window.g.fetch('/frm/notifications/unread', {}, { progress: false });
		if (res.success && res.error.code === 0) setBadge(res.data.unread || 0);
	})();
}
